import { useContext, useEffect, useRef, useState } from 'react'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import ErrorComponent from '@/components/error/error-component'
import { AuthContext } from '@/context/auth-context'

// A transient getOrganization() miss is the common case, so retry a few times quickly before
// giving up rather than waiting on root.tsx's slower visible/background refresh.
const kMaxAutoRetries = 3
const kRetryDelayMs = 2000

/**
 * Hold for an organization member whose organization data could not be loaded.
 *
 * getOrganization() resolves null on a transient fetch/parse miss, so create() can return an org
 * member with orgData == null. SubscriptionGuard routes that member here instead of trusting the
 * subscription getters, which would fall back to the member's individual JWT claim and could admit
 * them past an expired org.
 *
 * Most misses are transient, so we retry refreshUserAccountData() a few times with a short delay. A
 * successful refresh that loads org data swaps userAccountData and unmounts this component,
 * resolving to real access or OrgAccessDeniedPage. If the data is still unavailable after the
 * retries (a sustained outage), we surface an error with a reload instead of an indefinite spinner,
 * mirroring the Office pane (OutlookSession). The reload re-runs the full auth flow, which retries
 * getOrganization() via create(); root.tsx's background refresh can also recover and unmount this.
 */
export default function OrgDataUnavailableHold() {
  const { refreshUserAccountData } = useContext(AuthContext)
  const [attempts, setAttempts] = useState(0)
  const [showError, setShowError] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    // Set on setup, not just at init, so Strict Mode's mount/unmount/remount replay in development
    // restores the ref after its cleanup runs; otherwise the attempt counter stays disabled and the
    // loader never advances to the error screen.
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (showError) return
    if (attempts >= kMaxAutoRetries) {
      setShowError(true)
      return
    }

    const timer = setTimeout(async () => {
      await refreshUserAccountData()
      // A successful refresh unmounts this component before we get here; the guard avoids a state
      // update on an unmounted component when org data is still missing.
      if (mountedRef.current) setAttempts((n) => n + 1)
    }, kRetryDelayMs)

    return () => clearTimeout(timer)
  }, [attempts, showError, refreshUserAccountData])

  if (showError) {
    return (
      <ErrorComponent
        code="503"
        title="Can't load your organization"
        message="We couldn't load your organization account. Please try again in a moment."
        showRefreshButton={true}
      />
    )
  }

  return <FullPageLoader />
}
